/* eslint-disable global-require */

/*
 *  Layouts available:
 *
 *  Main - Layout with header and footer
 *  Empty - Layout without header and footer
 *
 */

import {CLASS_BOOKING_ROUTE} from 'utils/constants';

import App from './Pages/App';
import AddLocale from './Pages/AddLocale';
import CheckoutComRedirect from './Pages/Payments/CheckoutComRedirect';
import Locale from './Pages/Locale';
import Empty from './Pages/Empty';
import Main from './Pages/Main';
import MainWhite from './Pages/MainWhite';
import HomeNew from './Pages/Home2020/digital/HomeNewForDigital';
import SingleSignOn from './Pages/SingleSignOn';
import NotFound from './Pages/NotFound';
import AddTrailingSlash from './Pages/AddTrailingSlash';
import RemoveMultipleSlashes from './Pages/RemoveMultipleSlashes';

const isServer = typeof window === 'undefined';

const ENABLE_TIERED_REFERRAL = isServer ?
  process.env.ENABLE_TIERED_REFERRAL :
  window.ENABLE_TIERED_REFERRAL;

function lazyLoad(route) {
  if (typeof window === 'undefined') {
    route.load(() => {}); // preload all files
  }

  route.getComponent = (nextState, callback) => route.load(callback);
  // TODO - show loading indicator after first render
  return route;
}

const accountRoutes = [
  lazyLoad({
    path: 'account/bookings',
    layout: Main,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/Account/Bookings').default);
      });
    },
  }),
  lazyLoad({
    path: 'account/bookings/:id',
    layout: Main,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/Account/Bookings').default);
      });
    },
  }),
  lazyLoad({
    path: 'account/download-data',
    layout: Main,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/Account/DownloadData').default);
      });
    },
  }),
  lazyLoad({
    path: 'account/details',
    layout: Main,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/Account/AccountSettings').default);
      });
    },
  }),
  lazyLoad({
    path: 'account/details/edit',
    layout: Main,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/Account/EditProfile').default);
      });
    },
  }),
  lazyLoad({
    path: 'account/details/address/add',
    layout: Main,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/Account/AddAddress').default);
      });
    },
  }),
  lazyLoad({
    path: 'account/details/address/edit/:id',
    layout: Main,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/Account/EditAddress').default);
      });
    },
  }),
  lazyLoad({
    path: 'account/password',
    layout: Main,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/Account/Password').default);
      });
    },
  }),
  lazyLoad({
    path: 'account/plus',
    layout: Main,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/Account/Plus').default);
      });
    },
  }),
  lazyLoad({
    path: 'account/refer',
    layout: Main,
    load(callback) {
      require.ensure([], () => {
        if (ENABLE_TIERED_REFERRAL) {
          callback(null, require('./Pages/Account/TieredRefer').default);
        } else {
          callback(null, require('./Pages/Account/Refer').default);
        }
      });
    },
  }),
  lazyLoad({
    path: 'account/wallet',
    layout: Main,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/Account/Wallet').default);
      });
    },
  }),
  lazyLoad({
    path: 'account/wallet/add',
    layout: Main,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/Account/AddPaymentMethod').default);
      });
    },
  }),
];

const localizedRoutes = [
  ...accountRoutes,
  lazyLoad({
    path: 'account-blocked',
    layout: Main,
    isBlocked: true,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/AccountBlocked').default);
      });
    },
  }),
  lazyLoad({
    path: 'login',
    layout: Main,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/Login').default);
      });
    },
  }),
  lazyLoad({
    path: 'logout',
    layout: Main,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/Logout').default);
      });
    },
  }),
  lazyLoad({
    path: 'signup-campaign',
    layout: Main,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/PromotionSignup').default);
      });
    },
  }),
  lazyLoad({
    path: 'missbeez',
    layout: Empty,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/Landing/Missbeez').default);
      });
    },
  }),
  {
    path: 'sso',
    layout: Main,
    component: SingleSignOn,
  },
  lazyLoad({
    path: 'reset-password',
    layout: Main,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/ResetPassword').default);
      });
    },
  }),
  lazyLoad({
    path: 'signup',
    layout: Main,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/Signup').default);
      });
    },
  }),
  lazyLoad({
    path: 'about',
    layout: MainWhite,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/About').default);
      });
    },
  }),
  lazyLoad({
    path: 'cookies(/:platform)',
    layout: Main,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/Cookies').default);
      });
    },
  }),
  lazyLoad({
    path: 'discounts/:id',
    layout: Main,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/SchemeDiscounts').default);
      });
    },
  }),
  lazyLoad({
    path: 'events/:id',
    layout: Main,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/SchemePromo').default);
      });
    },
  }),
  lazyLoad({
    path: 'gifts',
    layout: Main,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/Gifts/Gifts').default);
      });
    },
  }),
  lazyLoad({
    path: 'gifts/checkout',
    layout: Main,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/Gifts/GiftsCheckout').default);
      });
    },
  }),
  lazyLoad({
    path: 'gifts/:id/receipt',
    layout: Main,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/Gifts/GiftsReceipt').default);
      });
    },
  }),
  lazyLoad({
    path: 'redeem-gift',
    layout: Main,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/Gifts/GiftsRedeem').default);
      });
    },
  }),
  lazyLoad({
    path: 'redeem-voucher',
    layout: Main,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/Gifts/GiftsRedeem').default);
      });
    },
  }),
  lazyLoad({
    path: 'reviews-testimonials',
    layout: Main,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/Testimonials').default);
      });
    },
  }),
  lazyLoad({
    path: 'service-etiquette',
    layout: Main,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/ServiceEtiquette').default);
      });
    },
  }),
  lazyLoad({
    path: 'stay-subscribed',
    layout: Main,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/StaySubscribed').default);
      });
    },
  }),
  lazyLoad({
    path: 'legal/july-2016-update',
    layout: Main,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/Legal/July2016Update').default);
      });
    },
  }),
  lazyLoad({
    path: 'legal',
    layout: Main,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/Legal').default);
      });
    },
  }),
  lazyLoad({
    path: 'wellpass-terms-and-conditions',
    layout: Main,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/WellpassTermsAndConditions').default);
      });
    },
  }),
  lazyLoad({
    path: 'privacy-policy(/:platform)',
    layout: Main,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/Privacy').default);
      });
    },
  }),
  lazyLoad({
    path: 'apps',
    layout: Main,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/Apps').default);
      });
    },
  }),
  lazyLoad({
    path: 'pricing',
    layout: Main,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/Pricing').default);
      });
    },
  }),
  lazyLoad({
    path: 'treatments',
    layout: MainWhite,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/Treatments/Treatments').default);
      });
    },
  }),
  lazyLoad({
    path: 'treatments/massage/couples-massage',
    layout: Main,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/Treatments/CouplesMassage').default);
      });
    },
  }),
  lazyLoad({
    path: 'treatments/:category/:stub',
    layout: Main,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/Treatments/Treatments').default);
      });
    },
  }),
  lazyLoad({
    path: 'treatments/:category',
    layout: MainWhite,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/Treatments/Treatments').default);
      });
    },
  }),
  lazyLoad({
    path: 'therapists',
    layout: Main,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/Therapists/Therapists').default);
      });
    },
  }),
  lazyLoad({
    path: 'therapists/:id',
    layout: Main,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/Therapists/Therapists').default);
      });
    },
  }),
  lazyLoad({
    path: 'team',
    layout: Main,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/Team/Team').default);
      });
    },
  }),
  lazyLoad({
    path: 'booking',
    layout: Empty,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/Booking').default);
      });
    },
  }),
  lazyLoad({
    path: CLASS_BOOKING_ROUTE,
    layout: Empty,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/Booking').default);
      });
    },
  }),
  lazyLoad({
    path: 'invite/:code',
    layout: Main,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/Invite/Invite').default);
      });
    },
  }),
  lazyLoad({
    path: 'referrals',
    layout: Main,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/ReferralInformation').default);
      });
    },
  }),
  lazyLoad({
    path: 'perkbox',
    layout: Main,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/Scheme').default);
      });
    },
  }),
  lazyLoad({
    path: 'regusuk',
    layout: Main,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/Scheme').default);
      });
    },
  }),
  lazyLoad({
    path: 'rewards',
    layout: Main,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/Rewards').default);
      });
    },
  }),
  lazyLoad({
    path: 'press',
    layout: Main,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/Press').default);
      });
    },
  }),
  lazyLoad({
    path: 'bookings/:id',
    layout: Main,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/SingleBooking/SingleBooking').default);
      });
    },
  }),
  lazyLoad({
    path: 'booking/confirm/:id',
    layout: Main,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/SingleBooking/SingleBooking').default);
      });
    },
  }),
  lazyLoad({
    path: 'video/:id',
    layout: Main,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/VideoCall').default);
      });
    },
  }),
  lazyLoad({
    path: 'account/bookings/:orderId/basket',
    layout: Empty,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/Ecommerce/EcommerceCheckout').default);
      });
    },
  }),
  lazyLoad({
    path: 'account/bookings/:orderId/basket/:basketId',
    layout: Empty,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/Ecommerce/EcommerceCheckout').default);
      });
    },
  }),
  lazyLoad({
    path: 'lp/:stub',
    layout: MainWhite,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/Home/Home').default);
      });
    },
  }),
  lazyLoad({
    path: 'lp2/:stub',
    layout: MainWhite,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/Home/Home').default);
      });
    },
  }),
  lazyLoad({
    path: 'lp3/:stub',
    layout: MainWhite,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/Home/Home').default);
      });
    },
  }),
  lazyLoad({
    path: 'lp4/:stub',
    layout: MainWhite,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/Home/Home').default);
      });
    },
  }),
  lazyLoad({
    path: 'lp5/:stub',
    layout: MainWhite,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/Landing/Verticals').default);
      });
    },
  }),
  lazyLoad({
    path: 'lp6/:stub',
    layout: MainWhite,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/Landing/Verticals').default);
      });
    },
  }),
  lazyLoad({
    path: 'lp-amp-2020/:stub',
    layout: MainWhite,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/Landing/Verticals').default);
      });
    },
  }),
  lazyLoad({
    path: 'ugc-terms',
    layout: Main,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/UgcTerms').default);
      });
    },
  }),
  lazyLoad({
    path: 'unsubscribe',
    layout: Main,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/Unsubscribe').default);
      });
    },
  }),
  lazyLoad({
    path: 'locations/:city(/:district)',
    layout: Main,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/City/CityContainer').default);
      });
    },
  }),
  lazyLoad({
    path: 'setup-account',
    layout: Main,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/SetupAccount').default);
      });
    },
  }),
  lazyLoad({
    path: 'one-time-login-failed',
    layout: Main,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/OneTimeLoginFailed').default);
      });
    },
  }),
  lazyLoad({
    path: 'osteopathy',
    layout: Main,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/Osteopathy').default);
      });
    },
  }),
  lazyLoad({
    path: 'nails',
    layout: Main,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/Nails').default);
      });
    },
  }),
  lazyLoad({
    path: 'mothers-day',
    layout: Main,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/MothersDay').default);
      });
    },
  }),
  lazyLoad({
    path: 'category-pre-launch/:stub',
    layout: Empty,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/CategoryPreLaunch').default);
      });
    },
  }),
  lazyLoad({
    path: 'hygiene-covid',
    isAppIframe: true,
    layout: Empty,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/HygieneCovid').default);
      });
    },
  }),
  lazyLoad({
    path: 'security',
    layout: Main,
    load(callback) {
      require.ensure([], () => {
        callback(null, require('./Pages/Security').default);
      });
    },
  }),
];

const childRoutes = localizedRoutes.map(({path}) => ({path}));

const routes = {
  path: '/',
  component: App,
  childRoutes: [
    {
      path: 'checkout-gateway-3ds-success-redirect',
      component: CheckoutComRedirect,
      childRoutes,
    },
    {
      path: 'checkout-gateway-3ds-failure-redirect',
      component: CheckoutComRedirect,
      childRoutes,
    },
    ...childRoutes.map(route => ({...route, component: AddLocale})),
    {
      path: '',
      component: AddLocale,
      layout: MainWhite,
      indexRoute: {
        component: HomeNew,
      },
      childRoutes: localizedRoutes,
    },
    {
      path: ':locale/',
      component: Locale,
      layout: MainWhite,
      indexRoute: {
        component: HomeNew,
      },
      childRoutes: localizedRoutes,
    },
    {
      path: ':locale//',
      component: RemoveMultipleSlashes,
      childRoutes,
    },
    {
      path: ':locale',
      component: AddTrailingSlash,
    },
    {
      path: ':locale/*',
      component: Locale,
      layout: Main,
      isNotFound: true,
      indexRoute: {
        component: NotFound,
      },
    },
    {
      path: '*',
      component: AddLocale,
      layout: Main,
      isNotFound: true,
      indexRoute: {
        component: NotFound,
      },
    },
  ],
};

export default routes;



// WEBPACK FOOTER //
// ./spa/routes.jsx